import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { TextingSetup } from '@/modules/texting/api/textingApi';

/** Command Center → Settings → Texting (agency admins only). */
export interface AdminTextingView extends TextingSetup {
  /** What the practice asked for, in full, while it waits on PracticeNova. */
  request: { e164: string; mode: string; forwardTo: string | null; requestedAt: string } | null;
  /** The EIN the practice gave on its registration form, when it did. */
  practiceEin: string | null;
}

export interface AdminNumberCheck {
  e164: string;
  onAccount: boolean;
  active: boolean;
  telnyxStatus: string | null;
  usedByOtherLocation: boolean;
  alreadyLive: boolean;
  differsFromRequestLast4: string | null;
  /** The messaging profile it is on now, when not this location's: connecting moves it. */
  otherProfileId: string | null;
}

export type RegistrationState =
  | 'not_submitted'
  | 'pending'
  | 'vetting'
  | 'approved'
  | 'rejected'
  | 'suspended';

export interface AdminRegistrationCheck {
  brand: {
    brandId: string;
    companyName: string | null;
    ein: string | null;
    state: RegistrationState;
  };
  campaign: {
    campaignId: string;
    state: RegistrationState;
    usecase: string | null;
    belongsToBrand: boolean;
  };
  practiceEin: string | null;
  einMatch: boolean | null;
  sharedWithPractices: number;
}

const base = (practiceId: string, locationId: string) =>
  `/agency/practices/${encodeURIComponent(practiceId)}/locations/${encodeURIComponent(locationId)}/texting`;
const unwrap = <T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> => p.then((r) => r.data.data);

export const textingAdminApi = {
  get: (practiceId: string, locationId: string) =>
    unwrap(apiClient.get<ApiResponse<AdminTextingView>>(base(practiceId, locationId))),
  checkNumber: (practiceId: string, locationId: string, e164: string) =>
    unwrap(
      apiClient.get<ApiResponse<AdminNumberCheck>>(`${base(practiceId, locationId)}/number-check`, {
        params: { e164 },
      }),
    ),
  connectNumber: (
    practiceId: string,
    locationId: string,
    e164: string,
    opts: { note?: string } = {},
  ) =>
    unwrap(
      apiClient.post<ApiResponse<AdminTextingView>>(`${base(practiceId, locationId)}/number`, {
        e164,
        ...(opts.note ? { note: opts.note } : {}),
      }),
    ),
  checkRegistration: (
    practiceId: string,
    locationId: string,
    brandId: string,
    campaignId: string,
  ) =>
    unwrap(
      apiClient.get<ApiResponse<AdminRegistrationCheck>>(
        `${base(practiceId, locationId)}/registration-check`,
        { params: { brandId, campaignId } },
      ),
    ),
  linkRegistration: (
    practiceId: string,
    locationId: string,
    input: { brandId: string; campaignId: string; confirmBrand?: boolean; note?: string },
  ) =>
    unwrap(
      apiClient.post<ApiResponse<AdminTextingView>>(
        `${base(practiceId, locationId)}/registration`,
        input,
      ),
    ),
  recheckRegistration: (practiceId: string, locationId: string) =>
    unwrap(
      apiClient.post<ApiResponse<AdminTextingView>>(
        `${base(practiceId, locationId)}/registration/check`,
      ),
    ),
};
